import { AgGridCellTooltipComponent, AgGridCellTooltipValueGetter } from 'components/AgGrid/AgGridCellTooltipComponent';
import type { BimWorkOrderChangePriceRevisionData, BimWorkOrderChangeTotalItemData } from 'gql/graphql';
import { BimChapterEnum } from 'gql/graphql';
import { bimNumberFormatter } from 'pages/Project/BimProject/BimCommonComponents/WorkOrderPriceCellRenderers';
import { formatDateToLocale, formatNumberToRu } from 'utils';

import type { ColDef, ColGroupDef, ValueFormatterParams } from 'ag-grid-community';
import { useMemo } from 'react';

import { BimWorkOrderRowDetailsType, BimWorkOrderRowTypeEnum } from '../../../WorkOrder/types';

import { getWorkOrderChangeValueFields } from './useBimWorkOrderChangeColumns';
import type { TGroupedItem } from './useBimWorkOrderChangePriceDetailsData';

const quantityFormatter = (p: ValueFormatterParams) => {
    const { value, data } = p;

    if (!value) {
        return '';
    }

    if (data?.name?.includes('в т.ч')) {
        return `${formatNumberToRu(value, 2)}%`;
    }
    if (data?.name?.includes('инфляции на')) {
        return `${formatNumberToRu(value, 4)}`;
    }

    return formatNumberToRu(value, 0);
};

export const useBimWorkOrderChangeDetailsColumns = ({
    baseColumns,
    detailsType,
    detailsRowType,
    chapter,
}: {
    baseColumns: (ColDef | ColGroupDef)[];
    detailsType: BimWorkOrderRowDetailsType | undefined;
    detailsRowType: BimWorkOrderRowTypeEnum | undefined;
    chapter: BimChapterEnum | undefined;
}) => {
    /*
     * состав сметы
     */
    const priceDetailsColumns = useMemo<ColDef<TGroupedItem>[]>(
        () => [
            {
                headerName: 'Шифр',
                field: 'planValue.code',
                minWidth: 230,
                suppressMovable: true,
                lockPosition: 'left',
                flex: 1,
            },
            {
                headerName: 'Наименование работ, затрат',
                field: 'planValue.name',
                minWidth: 180,
                suppressMovable: true,
                lockPosition: 'left',
                colSpan: ({ data }: { data: TGroupedItem | undefined }) => (data && 'isGroupTitle' in data ? 5 : 1),
                flex: 1,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
            },
            {
                headerName: 'Ед. изм',
                field: 'planValue.unit',
                width: 100,
            },
            {
                headerName: 'Кол-во',
                field: 'planValue.quantity',
                width: 100,
            },
            ...getWorkOrderChangeValueFields({
                valueKey: 'value',
                withCollapse: false,
            }),
        ],
        [],
    );

    /*
     * ревизии
     */
    const revisionColumns = useMemo<ColDef<BimWorkOrderChangePriceRevisionData>[]>(() => {
        if (detailsType !== BimWorkOrderRowDetailsType.Revision) {
            return [];
        }

        return baseColumns
            .filter((_, idx, fullArr) => idx === 0 || (idx !== 1 && idx !== fullArr.length - 1))
            .map((revisionColumn, index) => {
                if (index === 0) {
                    return {
                        ...revisionColumn,
                        width: 60,
                    };
                }

                if (
                    revisionColumn.headerName &&
                    ['План по НЗ', 'Выполнено по НЗ', 'Остаток по НЗ'].includes(revisionColumn.headerName) &&
                    'children' in revisionColumn
                ) {
                    return {
                        ...revisionColumn,
                        children: revisionColumn.children.filter(
                            (_, childIdx) => childIdx < revisionColumn.children.length - 2,
                        ),
                    };
                }

                return { ...revisionColumn, cellRenderer: undefined, filter: undefined };
            }) as ColGroupDef[];
    }, [baseColumns, detailsType]);

    /*
     * обновления
     */
    const updateColumns = useMemo<ColDef<BimWorkOrderChangePriceRevisionData>[]>(() => {
        if (detailsType !== BimWorkOrderRowDetailsType.Update) {
            return [];
        }

        return [
            {
                headerName: 'Дата изменения',
                field: 'priceUpdateDate',
                width: 240,
                spanHeaderHeight: true,
                valueFormatter: ({ value }: { value: string }) => formatDateToLocale(value),
            },
            ...(baseColumns
                .filter((_, idx, fullArr) => idx === 0 || (idx > 3 && idx !== fullArr.length - 1))
                .map((updateColumn, index) => {
                    if (index === 0) {
                        return {
                            ...updateColumn,
                            width: 60,
                        };
                    }

                    if (
                        updateColumn.headerName &&
                        ['План по НЗ', 'Выполнено по НЗ', 'Остаток по НЗ'].includes(updateColumn.headerName) &&
                        'children' in updateColumn
                    ) {
                        return {
                            ...updateColumn,
                            children: updateColumn.children.filter(
                                (_, childIdx) => childIdx < updateColumn.children.length - 2,
                            ),
                        };
                    }

                    return { ...updateColumn, cellRenderer: undefined, filter: undefined };
                }) as ColGroupDef[]),
        ];
    }, [baseColumns, detailsType]);

    /*
     * детализация итогов
     */
    const totalRowDetailsColumns = useMemo<ColDef<BimWorkOrderChangeTotalItemData>[]>(() => {
        const hideQuantity = chapter !== BimChapterEnum.Chapter9 && chapter !== BimChapterEnum.Chapter12;

        return [
            {
                headerName: 'Наименование работ, затрат',
                field: 'name',
                suppressMovable: true,
                lockPosition: 'left',
                flex: 1,
                minWidth: 340,
            },
            {
                headerName: 'Кол-во',
                field: 'quantity',
                width: 100,
                hide: hideQuantity,
                cellClass: 'secondaryRow',
                valueFormatter: quantityFormatter,
            },
            {
                headerName: 'План по НЗ',
                marryChildren: true,
                children: getWorkOrderChangeValueFields({
                    valueKey: 'planValue',
                    withCollapse: false,
                    useBoldStyle: true,
                }),
            },
            {
                headerName: 'Выполнено по НЗ',
                field: 'completedValue.total',
                spanHeaderHeight: true,
                valueFormatter: bimNumberFormatter(0),
                cellClass: 'ag-right-aligned-cell boldWeight',
                width: 150,
            },
            {
                headerName: 'Остаток НЗ',
                field: 'remainderValue.total',
                spanHeaderHeight: true,
                valueFormatter: bimNumberFormatter(0),
                cellClass: 'ag-right-aligned-cell boldWeight',
                width: 150,
            },
        ];
    }, [chapter]);

    /*
     * общая информация
     */
    const detailsColumns = useMemo(() => {
        if (detailsType === BimWorkOrderRowDetailsType.Update) {
            return updateColumns;
        }
        if (detailsType === BimWorkOrderRowDetailsType.Revision) {
            return revisionColumns;
        }
        if (detailsRowType === BimWorkOrderRowTypeEnum.TotalsString) {
            return totalRowDetailsColumns;
        }

        return priceDetailsColumns;
    }, [detailsRowType, detailsType, priceDetailsColumns, revisionColumns, totalRowDetailsColumns, updateColumns]);

    return {
        detailsColumns,
    };
};
